import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';

interface CMVRecalculationContextType {
  isRecalculating: boolean;
  recalculationMessage: string | null;
  setManualRecalculating: (value: boolean, message?: string) => void;
}

const CMVRecalculationContext = createContext<CMVRecalculationContextType>({
  isRecalculating: false,
  recalculationMessage: null,
  setManualRecalculating: () => {},
});

export const useCMVRecalculation = () => useContext(CMVRecalculationContext);

interface CMVRecalculationProviderProps {
  children: ReactNode;
}

export const CMVRecalculationProvider = ({ children }: CMVRecalculationProviderProps) => {
  const [manualRecalculating, setManualRecalculating] = useState(false);
  const [manualMessage, setManualMessage] = useState<string | null>(null);
  const [recalculationMessage, setRecalculationMessage] = useState<string | null>(null);
  const [debouncedRecalculating, setDebouncedRecalculating] = useState(false);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Detect when critical queries are fetching
  const isFetchingIngredients = useIsFetching({ queryKey: ['ingredients'] }) > 0;
  const isFetchingRecipes = useIsFetching({ queryKey: ['recipes'] }) > 0;
  const isFetchingMenuItems = useIsFetching({ queryKey: ['menu-items'] }) > 0;
  const isFetchingDashboard = useIsFetching({ queryKey: ['dashboard-data'] }) > 0;
  const isFetchingStock = useIsFetching({ queryKey: ['stock_movements'] }) > 0;
  
  // Detect if there are active mutations (indicates real data changes, not initial loads)
  const isMutating = useIsMutating() > 0;
  
  // Combine all recalculation states
  const isAutoRecalculating = isFetchingIngredients || isFetchingRecipes || isFetchingMenuItems || isFetchingDashboard || isFetchingStock;
  
  // Only show indicator when there's an active mutation causing the fetch (not initial load)
  const shouldShowIndicator = manualRecalculating || (isAutoRecalculating && isMutating);
  
  // Debounce the indicator to avoid flicker (300ms delay)
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    if (shouldShowIndicator) {
      debounceTimerRef.current = setTimeout(() => {
        setDebouncedRecalculating(true);
      }, 300);
    } else {
      // Hide immediately when done
      setDebouncedRecalculating(false);
    }
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [shouldShowIndicator]);
  
  useEffect(() => {
    if (!debouncedRecalculating) {
      setRecalculationMessage(null);
      return;
    }
    
    if (manualRecalculating && manualMessage) {
      setRecalculationMessage(manualMessage);
    } else if (isFetchingIngredients) {
      setRecalculationMessage('Atualizando custos de insumos...');
    } else if (isFetchingRecipes) {
      setRecalculationMessage('Recalculando CMV das receitas...');
    } else if (isFetchingMenuItems) {
      setRecalculationMessage('Atualizando preços do cardápio...');
    } else if (isFetchingStock) {
      setRecalculationMessage('Sincronizando estoque...');
    } else if (isFetchingDashboard) {
      setRecalculationMessage('Atualizando dashboard...');
    }
  }, [isFetchingIngredients, isFetchingRecipes, isFetchingMenuItems, isFetchingDashboard, isFetchingStock, manualRecalculating, manualMessage, debouncedRecalculating]);
  
  const handleSetManualRecalculating = (value: boolean, message?: string) => {
    setManualRecalculating(value);
    setManualMessage(message || null);
  };
  
  return (
    <CMVRecalculationContext.Provider value={{ 
      isRecalculating: debouncedRecalculating, 
      recalculationMessage,
      setManualRecalculating: handleSetManualRecalculating,
    }}>
      {children}
    </CMVRecalculationContext.Provider>
  );
};
